import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import {
  Globe, Lock, Server, Cookie, FileText, Bot, Gauge, MapPin,
  Link2, ArrowRight, Type, Activity, Network, Route, Leaf,
  HardDrive, Search, Info, ShieldCheck, Layers, Share2, Mail,
  Flame, Shield, Archive, TrendingUp, Ban, Bug, Key, Settings,
  Camera, ExternalLink, ArrowLeft, Database, Radio, Wifi,
  FileSearch, AlertTriangle, Clock, Zap, Globe2, Monitor
} from 'lucide-react';

const FeatureSection = ({ id, title, icon: Icon, description, useCases, links }) => (
  <section id={id} className="mb-12 scroll-mt-32">
    <div className="bg-[#1a1a2e] rounded-xl border border-gray-800/50 overflow-hidden">
      <div className="p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-red-600/20 rounded-xl">
            <Icon className="h-6 w-6 text-red-500" />
          </div>
          <h3 className="text-xl font-bold text-white">{title}</h3>
        </div>
        
        <div className="space-y-4 text-gray-300">
          <div>
            <h4 className="text-sm font-semibold text-red-400 uppercase tracking-wide mb-2">Descrizione</h4>
            <p className="text-sm leading-relaxed">{description}</p>
          </div>
          
          {useCases && (
            <div>
              <h4 className="text-sm font-semibold text-red-400 uppercase tracking-wide mb-2">Casi d'Uso</h4>
              <p className="text-sm leading-relaxed">{useCases}</p>
            </div>
          )}
          
          {links && links.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-red-400 uppercase tracking-wide mb-2">Link Utili</h4>
              <ul className="space-y-1">
                {links.map((link, idx) => (
                  <li key={idx}>
                    <a 
                      href={link.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                    >
                      <ExternalLink className="h-3 w-3" />
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  </section>
);

const AboutPage = () => {
  const features = [
    {
      id: 'ip-info',
      title: 'IP Info',
      icon: Globe,
      description: "Un indirizzo IP (Internet Protocol address) è un'etichetta numerica assegnata a ogni dispositivo connesso a una rete/internet. L'IP associato a un dato dominio può essere trovato interrogando il Domain Name System (DNS) per il record A (address) del dominio.",
      useCases: "Trovare l'IP di un dato server è il primo passo per condurre ulteriori indagini, poiché ci permette di sondare il server per informazioni aggiuntive. Include la creazione di una mappa dettagliata dell'infrastruttura di rete di un obiettivo, l'individuazione della posizione fisica di un server, l'identificazione del servizio di hosting e la scoperta di altri domini ospitati sullo stesso indirizzo IP.",
      links: [
        { name: 'Understanding IP Addresses', url: 'https://www.digitalocean.com/community/tutorials/understanding-ip-addresses-subnets-and-cidr-notation-for-networking' },
        { name: 'IP Addresses - Wiki', url: 'https://en.wikipedia.org/wiki/IP_address' }
      ]
    },
    {
      id: 'ssl-chain',
      title: 'SSL Chain',
      icon: Lock,
      description: "I certificati SSL sono certificati digitali che autenticano l'identità di un sito web o server, abilitano comunicazioni crittografate sicure (HTTPS) e stabiliscono fiducia tra client e server. Un certificato SSL valido è necessario per un sito web per poter utilizzare il protocollo HTTPS e crittografare i dati utente + sito in transito.",
      useCases: "I certificati SSL non solo forniscono la garanzia che la trasmissione dei dati da e verso il sito web sia sicura, ma forniscono anche dati OSINT preziosi. Le informazioni da un certificato SSL possono includere l'autorità emittente, il nome del dominio, il periodo di validità e talvolta anche i dettagli dell'organizzazione.",
      links: [
        { name: 'TLS - Wiki', url: 'https://en.wikipedia.org/wiki/Transport_Layer_Security' },
        { name: 'What is SSL (Cloudflare)', url: 'https://www.cloudflare.com/learning/ssl/what-is-ssl/' },
        { name: 'SSL Checker', url: 'https://www.sslshopper.com/ssl-checker.html' }
      ]
    },
    {
      id: 'dns-records',
      title: 'DNS Records',
      icon: Server,
      description: "Questo task comporta la ricerca dei record DNS associati a un dominio specifico. Il DNS è un sistema che traduce i nomi di dominio leggibili dall'uomo in indirizzi IP che i computer usano per comunicare. Esistono vari tipi di record DNS, inclusi A (address), MX (mail exchange), NS (name server), CNAME (canonical name) e TXT (text).",
      useCases: "L'estrazione dei record DNS può fornire una ricchezza di informazioni in un'indagine OSINT. I record A e AAAA possono rivelare indirizzi IP associati a un dominio. I record MX possono dare indizi sul provider email del dominio. I record TXT sono spesso usati per vari scopi amministrativi e possono talvolta rivelare informazioni interne.",
      links: [
        { name: 'What are DNS records? (Cloudflare)', url: 'https://www.cloudflare.com/learning/dns/dns-records/' },
        { name: 'DNS Record Types', url: 'https://en.wikipedia.org/wiki/List_of_DNS_record_types' },
        { name: 'DNS Lookup (MxToolbox)', url: 'https://mxtoolbox.com/DNSLookup.aspx' }
      ]
    },
    {
      id: 'cookies',
      title: 'Cookies',
      icon: Cookie,
      description: "Il task Cookies comporta l'esame dei cookie HTTP impostati dal sito web di destinazione. I cookie sono piccoli pezzi di dati memorizzati sul computer dell'utente dal browser web durante la navigazione di un sito web. Contengono una modesta quantità di dati specifici per un particolare client e sito web, come preferenze del sito, stato della sessione o informazioni di tracciamento.",
      useCases: "I cookie possono rivelare informazioni su come il sito web traccia e interagisce con i suoi utenti. I cookie di sessione possono rivelare come vengono gestite le sessioni utente, e i cookie di tracciamento possono indicare quali framework di tracciamento o analytics vengono utilizzati.",
      links: [
        { name: 'HTTP Cookie Docs (Mozilla)', url: 'https://developer.mozilla.org/en-US/docs/Web/HTTP/Cookies' },
        { name: 'What are Cookies (Cloudflare)', url: 'https://www.cloudflare.com/learning/privacy/what-are-cookies/' }
      ]
    },
    {
      id: 'crawl-rules',
      title: 'Crawl Rules (Robots.txt)',
      icon: Bot,
      description: "Robots.txt è un file che si trova (di solito) alla radice di un dominio, ed è usato per implementare il Robots Exclusion Protocol (REP) per indicare quali pagine dovrebbero essere ignorate da quali crawler e bot. È buona pratica evitare che i crawler dei motori di ricerca sovraccarichino il tuo sito.",
      useCases: "È spesso utile controllare il file robots.txt durante un'indagine, poiché può talvolta rivelare le directory e le pagine che il proprietario del sito non vuole siano indicizzate, potenzialmente perché contengono informazioni sensibili, o rivelare l'esistenza di directory altrimenti nascoste o non collegate.",
      links: [
        { name: 'Google Search Docs - Robots.txt', url: 'https://developers.google.com/search/docs/advanced/robots/intro' },
        { name: 'Robots.txt - Wiki', url: 'https://en.wikipedia.org/wiki/Robots_exclusion_standard' }
      ]
    },
    {
      id: 'headers',
      title: 'HTTP Headers',
      icon: FileText,
      description: "Il task Headers comporta l'estrazione e l'interpretazione degli header HTTP inviati dal sito web di destinazione durante il ciclo richiesta-risposta. Gli header HTTP sono coppie chiave-valore inviate all'inizio di una risposta HTTP, o prima dei dati effettivi. Gli header contengono direttive importanti su come gestire i dati trasferiti.",
      useCases: "L'analisi degli header HTTP può fornire informazioni significative in un'indagine OSINT. Gli header possono rivelare configurazioni specifiche del server, tecnologie scelte, direttive di caching e varie impostazioni di sicurezza.",
      links: [
        { name: 'HTTP Headers - Docs', url: 'https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers' },
        { name: 'OWASP Secure Headers Project', url: 'https://owasp.org/www-project-secure-headers/' }
      ]
    },
    {
      id: 'quality-metrics',
      title: 'Quality Metrics',
      icon: Gauge,
      description: "Usando Lighthouse, il task Quality Metrics misura le prestazioni, l'accessibilità, le best practice e il SEO del sito web di destinazione. Questo restituisce una semplice checklist di 100 metriche core, insieme a un punteggio per ogni categoria, per valutare la qualità complessiva di un dato sito.",
      useCases: "Utile per valutare la salute tecnica di un sito, problemi SEO, identificare vulnerabilità e garantire la conformità con gli standard.",
      links: [
        { name: 'Lighthouse Docs', url: 'https://developer.chrome.com/docs/lighthouse/' },
        { name: 'Google Page Speed Tools', url: 'https://developers.google.com/speed' },
        { name: 'SEO Checker', url: 'https://www.seobility.net/en/seocheck/' }
      ]
    },
    {
      id: 'server-location',
      title: 'Server Location',
      icon: MapPin,
      description: "Il task Server Location determina la posizione fisica del server che ospita un dato sito web basandosi sul suo indirizzo IP. Questo viene fatto cercando l'IP in un database di localizzazione, che mappa l'IP a latitudine e longitudine di data center e ISP conosciuti.",
      useCases: "Conoscere la posizione del server è un buon primo passo per comprendere meglio un sito web. Per i proprietari di siti aiuta nell'ottimizzazione della consegna dei contenuti, garantendo la conformità con i requisiti di residenza dei dati, e identificando potenziali problemi di latenza.",
      links: [
        { name: 'IP Locator', url: 'https://geobytes.com/iplocator/' },
        { name: 'Internet Geolocation - Wiki', url: 'https://en.wikipedia.org/wiki/Internet_geolocation' }
      ]
    },
    {
      id: 'associated-hosts',
      title: 'Associated Hosts',
      icon: Network,
      description: "Questo task comporta l'identificazione e l'elenco di tutti i domini e sottodomini (hostname) associati al dominio primario del sito web. Questo processo spesso comporta l'enumerazione DNS per scoprire qualsiasi dominio collegato e hostname.",
      useCases: "Durante un'indagine, comprendere la portata completa della presenza web di un obiettivo è critico. I domini associati potrebbero portare a scoprire progetti correlati, siti di backup, siti di sviluppo/test o servizi collegati al sito principale.",
      links: [
        { name: 'DNS Enumeration - Wiki', url: 'https://en.wikipedia.org/wiki/DNS_enumeration' },
        { name: 'DNS Dumpster', url: 'https://dnsdumpster.com/' },
        { name: 'Subdomain Finder', url: 'https://subdomainfinder.c99.nl/' }
      ]
    },
    {
      id: 'redirect-chain',
      title: 'Redirect Chain',
      icon: ArrowRight,
      description: "Questo task traccia la sequenza di redirect HTTP che si verificano dall'URL originale all'URL di destinazione finale. Un redirect HTTP è una risposta con un codice di stato che consiglia al client di andare a un altro URL. I redirect possono verificarsi per diversi motivi, come normalizzazione URL, applicazione di HTTPS, URL shortener o inoltro utenti.",
      useCases: "Comprendere la catena di redirect può essere utile per diversi motivi. Dal punto di vista della sicurezza, catene di redirect lunghe o complicate possono essere un segno di potenziali rischi di sicurezza, come redirect non crittografati nella catena.",
      links: [
        { name: 'HTTP Redirects - MDN', url: 'https://developer.mozilla.org/en-US/docs/Web/HTTP/Redirections' },
        { name: 'URL Redirection - Wiki', url: 'https://en.wikipedia.org/wiki/URL_redirection' }
      ]
    },
    {
      id: 'txt-records',
      title: 'TXT Records',
      icon: Type,
      description: "I record TXT sono un tipo di record DNS che fornisce informazioni testuali a fonti esterne al tuo dominio. Possono essere usati per una varietà di scopi, come la verifica della proprietà del dominio, la sicurezza email e persino la prevenzione di modifiche non autorizzate al tuo sito web.",
      useCases: "I record TXT spesso rivelano quali servizi esterni e tecnologie vengono utilizzati con un dato dominio. Possono rivelare dettagli sulla configurazione email del dominio, l'uso di servizi specifici come Google Workspace o Microsoft 365, o misure di sicurezza come SPF e DKIM.",
      links: [
        { name: 'TXT Records (Cloudflare)', url: 'https://www.cloudflare.com/learning/dns/dns-records/dns-txt-record/' },
        { name: 'TXT Record Lookup (MxToolbox)', url: 'https://mxtoolbox.com/TXTLookup.aspx' }
      ]
    },
    {
      id: 'server-status',
      title: 'Server Status',
      icon: Activity,
      description: "Verifica se un server è online e risponde alle richieste. Mostra il codice di stato HTTP, il tempo di risposta e la percentuale di uptime.",
      useCases: "Utile per monitorare la disponibilità di un sito web e identificare potenziali problemi di accessibilità o performance."
    },
    {
      id: 'open-ports',
      title: 'Open Ports',
      icon: Radio,
      description: "Le porte aperte su un server sono endpoint di comunicazione disponibili per stabilire connessioni con i client. Ogni porta corrisponde a un servizio o protocollo specifico, come HTTP (porta 80), HTTPS (porta 443), FTP (porta 21), ecc. Le porte aperte possono essere determinate usando tecniche come la scansione delle porte.",
      useCases: "Sapere quali porte sono aperte su un server può fornire informazioni sui servizi in esecuzione su quel server, utile per comprendere le potenziali vulnerabilità del sistema.",
      links: [
        { name: 'List of TCP & UDP Port Numbers', url: 'https://en.wikipedia.org/wiki/List_of_TCP_and_UDP_port_numbers' },
        { name: 'NMAP - Port Scanning Basics', url: 'https://nmap.org/book/man-port-scanning-basics.html' }
      ]
    },
    {
      id: 'traceroute',
      title: 'Traceroute',
      icon: Route,
      description: "Traceroute è uno strumento diagnostico di rete usato per tracciare in tempo reale il percorso seguito da un pacchetto di informazioni da un sistema a un altro. Registra ogni hop lungo il percorso, fornendo dettagli sugli IP dei router e il ritardo in ogni punto.",
      useCases: "Nelle indagini OSINT, traceroute può fornire informazioni sui percorsi di routing e la geografia dell'infrastruttura di rete che supporta un sito web o servizio. Questo può aiutare a identificare colli di bottiglia di rete, potenziale censura o manipolazione del traffico di rete.",
      links: [
        { name: 'What is Traceroute (Cloudflare)', url: 'https://www.cloudflare.com/learning/network-layer/what-is-traceroute/' }
      ]
    },
    {
      id: 'carbon-footprint',
      title: 'Carbon Footprint',
      icon: Leaf,
      description: "Questa analisi calcola l'impronta di carbonio approssimativa di un sito web. I siti web consumano energia attraverso i server che li ospitano, le reti che trasmettono i dati e i dispositivi degli utenti finali. Siti più efficienti e ottimizzati tendono ad avere un'impronta di carbonio inferiore.",
      useCases: "Utile per valutare l'impatto ambientale di un sito web e identificare opportunità di ottimizzazione per ridurre il consumo energetico e le emissioni di CO2.",
      links: [
        { name: 'Website Carbon Calculator', url: 'https://www.websitecarbon.com/' },
        { name: 'The Green Web Foundation', url: 'https://www.thegreenwebfoundation.org/' }
      ]
    },
    {
      id: 'server-info',
      title: 'Server Info',
      icon: HardDrive,
      description: "Informazioni sul server web che ospita il sito, incluso il software del server (Apache, Nginx, IIS, ecc.), versione, sistema operativo e altre configurazioni rilevabili dagli header HTTP o altre fonti.",
      useCases: "Conoscere il software del server può rivelare potenziali vulnerabilità note per quella versione specifica, o fornire indicazioni sullo stack tecnologico utilizzato."
    },
    {
      id: 'whois-lookup',
      title: 'Whois Lookup',
      icon: Search,
      description: "WHOIS è un protocollo di query e risposta ampiamente usato per interrogare database che memorizzano i proprietari registrati di un nome di dominio o blocco di indirizzi IP. Le informazioni WHOIS includono tipicamente il registrant, il registrar, le date di registrazione e scadenza, e i nameserver.",
      useCases: "Le ricerche WHOIS sono fondamentali nelle indagini OSINT per identificare i proprietari dei domini, tracciare la storia del dominio, verificare la legittimità di un sito e raccogliere informazioni di contatto.",
      links: [
        { name: 'WHOIS Lookup (DomainTools)', url: 'https://whois.domaintools.com/' },
        { name: 'ICANN WHOIS', url: 'https://lookup.icann.org/' }
      ]
    },
    {
      id: 'domain-info',
      title: 'Domain Info',
      icon: Info,
      description: "Informazioni generali sul dominio, inclusa l'età del dominio, la data di creazione, la data di scadenza, il registrar e altre informazioni rilevanti estratte dai record WHOIS e altre fonti.",
      useCases: "L'età e la storia del dominio possono essere indicatori della reputazione e affidabilità di un sito web. I domini più vecchi tendono ad essere più affidabili."
    },
    {
      id: 'dnssec',
      title: 'DNS Security Extensions (DNSSEC)',
      icon: ShieldCheck,
      description: "DNSSEC (Domain Name System Security Extensions) è una suite di specifiche per proteggere certi tipi di informazioni fornite dal DNS. Aggiunge firme crittografiche ai record DNS esistenti, permettendo ai resolver di verificare che le risposte DNS non siano state manomesse.",
      useCases: "Verificare se DNSSEC è abilitato aiuta a valutare la postura di sicurezza di un dominio e la sua protezione contro attacchi DNS spoofing e cache poisoning.",
      links: [
        { name: 'DNSSEC - Cloudflare', url: 'https://www.cloudflare.com/dns/dnssec/how-dnssec-works/' },
        { name: 'DNSSEC Analyzer', url: 'https://dnssec-analyzer.verisignlabs.com/' }
      ]
    },
    {
      id: 'site-features',
      title: 'Site Features',
      icon: Layers,
      description: "Analisi delle funzionalità e caratteristiche implementate sul sito web, come form di login, moduli di pagamento, chat live, newsletter, social login, e altre funzionalità interattive.",
      useCases: "Identificare le funzionalità del sito può rivelare potenziali vettori di attacco (form di login, elaborazione pagamenti) e fornire informazioni sulla complessità e lo scopo del sito."
    },
    {
      id: 'hsts',
      title: 'HTTP Strict Transport Security (HSTS)',
      icon: Shield,
      description: "HSTS è un meccanismo di policy di sicurezza web che aiuta a proteggere i siti web contro attacchi man-in-the-middle come protocol downgrade e cookie hijacking. Permette ai server web di dichiarare che i browser web dovrebbero interagire con esso solo usando connessioni HTTPS sicure.",
      useCases: "Verificare HSTS è importante per valutare la sicurezza delle comunicazioni con il sito web. Un sito con HSTS configurato correttamente forza l'uso di HTTPS, proteggendo gli utenti.",
      links: [
        { name: 'HSTS Docs (Mozilla)', url: 'https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Strict-Transport-Security' },
        { name: 'HSTS Preload List', url: 'https://hstspreload.org/' }
      ]
    },
    {
      id: 'dns-server',
      title: 'DNS Server',
      icon: Database,
      description: "Informazioni sui nameserver DNS autoritativi per il dominio. Questi server sono responsabili di rispondere alle query DNS per il dominio e i suoi sottodomini.",
      useCases: "Identificare i nameserver può rivelare il provider DNS utilizzato (Cloudflare, AWS Route53, Google Cloud DNS, ecc.) e potenziali punti di vulnerabilità."
    },
    {
      id: 'tech-stack',
      title: 'Tech Stack',
      icon: Layers,
      description: "Identificazione delle tecnologie utilizzate dal sito web, inclusi framework frontend (React, Vue, Angular), CMS (WordPress, Drupal), server web, CDN, servizi analytics, librerie JavaScript e altre tecnologie.",
      useCases: "Conoscere lo stack tecnologico di un sito può rivelare potenziali vulnerabilità associate a versioni specifiche di software, e fornisce informazioni preziose per la ricognizione.",
      links: [
        { name: 'BuiltWith', url: 'https://builtwith.com/' },
        { name: 'Wappalyzer', url: 'https://www.wappalyzer.com/' }
      ]
    },
    {
      id: 'listed-pages',
      title: 'Listed Pages (Sitemap)',
      icon: FileSearch,
      description: "Analisi della sitemap XML del sito web per identificare tutte le pagine pubblicamente elencate. Le sitemap aiutano i motori di ricerca a scoprire e indicizzare le pagine di un sito.",
      useCases: "Esaminare la sitemap può rivelare la struttura del sito, pagine nascoste ma indicizzate, e la frequenza di aggiornamento dei contenuti."
    },
    {
      id: 'security-txt',
      title: 'Security.txt',
      icon: FileText,
      description: "Security.txt è uno standard proposto (RFC 9116) che permette ai siti web di definire policy di sicurezza e informazioni di contatto per i ricercatori di sicurezza. Il file è tipicamente posizionato in /.well-known/security.txt.",
      useCases: "La presenza di un security.txt indica che l'organizzazione ha un programma di responsible disclosure e fornisce canali appropriati per segnalare vulnerabilità.",
      links: [
        { name: 'Security.txt RFC', url: 'https://securitytxt.org/' },
        { name: 'RFC 9116', url: 'https://www.rfc-editor.org/rfc/rfc9116' }
      ]
    },
    {
      id: 'linked-pages',
      title: 'Linked Pages',
      icon: Link2,
      description: "Analisi dei link presenti sul sito web, sia interni (verso altre pagine dello stesso dominio) che esterni (verso altri domini). Include anche l'identificazione di link rotti.",
      useCases: "L'analisi dei link può rivelare relazioni con altri siti, dipendenze da servizi esterni, e potenziali problemi SEO dovuti a link rotti."
    },
    {
      id: 'social-tags',
      title: 'Social Tags (Open Graph)',
      icon: Share2,
      description: "Meta tag utilizzati per controllare come il contenuto appare quando condiviso sui social media. Include Open Graph tags (Facebook), Twitter Cards e altri meta tag sociali.",
      useCases: "Esaminare i social tags può rivelare come il sito si presenta sui social media e fornisce informazioni su come l'organizzazione descrive se stessa.",
      links: [
        { name: 'Open Graph Protocol', url: 'https://ogp.me/' },
        { name: 'Twitter Cards', url: 'https://developer.twitter.com/en/docs/twitter-for-websites/cards/overview/abouts-cards' }
      ]
    },
    {
      id: 'email-config',
      title: 'Email Configuration',
      icon: Mail,
      description: "Analisi della configurazione email del dominio, inclusi SPF (Sender Policy Framework), DKIM (DomainKeys Identified Mail), DMARC (Domain-based Message Authentication), e BIMI (Brand Indicators for Message Identification).",
      useCases: "Queste informazioni aiutano a valutare la postura di sicurezza email del dominio, identificare potenziali vulnerabilità e verificare la legittimità delle email per il rilevamento di phishing.",
      links: [
        { name: 'SPF/DKIM/DMARC (Cloudflare)', url: 'https://www.cloudflare.com/learning/email-security/dmarc-dkim-spf/' },
        { name: 'MX Toolbox', url: 'https://mxtoolbox.com/' },
        { name: 'EasyDMARC Scanner', url: 'https://easydmarc.com/tools/domain-scanner' }
      ]
    },
    {
      id: 'firewall-detection',
      title: 'Firewall Detection (WAF)',
      icon: Flame,
      description: "Un WAF (Web Application Firewall) aiuta a proteggere le applicazioni web filtrando e monitorando il traffico HTTP tra un'applicazione web e Internet. Protegge tipicamente da attacchi come cross-site forgery, XSS, file inclusion e SQL injection.",
      useCases: "È utile capire se un sito usa un WAF e quale software/servizio firewall utilizza, poiché questo fornisce informazioni sulla protezione del sito contro diversi vettori di attacco, ma può anche rivelare vulnerabilità nel firewall stesso.",
      links: [
        { name: 'What is a WAF (Cloudflare)', url: 'https://www.cloudflare.com/learning/ddos/glossary/web-application-firewall-waf/' },
        { name: 'OWASP - WAFs', url: 'https://owasp.org/www-community/Web_Application_Firewall' }
      ]
    },
    {
      id: 'http-security',
      title: 'HTTP Security Features',
      icon: Shield,
      description: "Header di sicurezza HTTP configurati correttamente aggiungono uno strato di protezione contro attacchi comuni. Include HSTS, Content Security Policy (CSP), X-Content-Type-Options, X-Frame-Options e altri header di sicurezza.",
      useCases: "Rivedere gli header di sicurezza è importante, poiché offre informazioni sulla postura difensiva di un sito e potenziali vulnerabilità, permettendo mitigazione proattiva e garantendo conformità con le best practice.",
      links: [
        { name: 'OWASP Secure Headers', url: 'https://owasp.org/www-project-secure-headers/' },
        { name: 'Security Headers Checker', url: 'https://securityheaders.com/' },
        { name: 'Mozilla Observatory', url: 'https://observatory.mozilla.org/' }
      ]
    },
    {
      id: 'archive-history',
      title: 'Archive History',
      icon: Archive,
      description: "Recupera la storia completa degli archivi dalla Wayback Machine di Internet Archive. Mostra quando il sito è stato catturato per la prima volta, l'ultima cattura e il numero totale di snapshot.",
      useCases: "Utile per comprendere la storia di un sito e come è cambiato nel tempo. Può anche essere utile per trovare vecchie versioni di un sito o contenuti che sono stati rimossi.",
      links: [
        { name: 'Wayback Machine', url: 'https://archive.org/web/' }
      ]
    },
    {
      id: 'global-ranking',
      title: 'Global Ranking',
      icon: TrendingUp,
      description: "Mostra il ranking globale del sito richiesto. Questo è accurato solo per i siti web che sono nella top 100 milioni. I dati provengono dal progetto Tranco, che raccoglie i top siti dal web da Umbrella, Majestic, Quantcast, Chrome UX Report e Cloudflare Radar.",
      useCases: "Conoscere il ranking globale di un sito può essere utile per comprendere la scala del sito e per confrontarlo con altri siti. Aiuta anche a comprendere la popolarità relativa.",
      links: [
        { name: 'Tranco List', url: 'https://tranco-list.eu/' },
        { name: 'Similar Web', url: 'https://www.similarweb.com/' }
      ]
    },
    {
      id: 'block-detection',
      title: 'Block Detection',
      icon: Ban,
      description: "Verifica l'accesso all'URL utilizzando più di 10 dei più popolari server DNS di privacy, malware e parental control. Determina se il sito è bloccato da questi servizi.",
      useCases: "Utile per capire se un sito è considerato pericoloso o inappropriato da vari servizi di filtraggio DNS.",
      links: [
        { name: 'ThreatJammer Lists', url: 'https://threatjammer.com/osint-lists' }
      ]
    },
    {
      id: 'malware-phishing',
      title: 'Malware & Phishing Detection',
      icon: Bug,
      description: "Verifica se un sito appare in diverse liste comuni di malware e phishing per determinare il suo livello di minaccia. Include verifiche contro Google Safe Browsing, VirusTotal e altre fonti.",
      useCases: "Sapere se un sito è elencato come minaccia da uno di questi servizi può essere utile per comprendere la reputazione di un sito e identificare potenziali rischi.",
      links: [
        { name: 'URLHaus', url: 'https://urlhaus-api.abuse.ch/' },
        { name: 'PhishTank', url: 'https://www.phishtank.com/' },
        { name: 'VirusTotal', url: 'https://www.virustotal.com/' }
      ]
    },
    {
      id: 'tls-cipher',
      title: 'TLS Cipher Suites',
      icon: Key,
      description: "Combinazioni di algoritmi crittografici usati dal server per stabilire una connessione sicura. Include l'algoritmo di scambio chiavi, algoritmo di crittografia bulk, algoritmo MAC e PRF (pseudorandom function).",
      useCases: "Questa è un'informazione importante da testare dal punto di vista della sicurezza. Una cipher suite è sicura quanto gli algoritmi che contiene. Se la versione di crittografia ha vulnerabilità note, la connessione TLS può essere vulnerabile ad attacchi.",
      links: [
        { name: 'sslscan2 CLI', url: 'https://github.com/rbsec/sslscan' },
        { name: 'SSL Labs Test', url: 'https://www.ssllabs.com/ssltest/' }
      ]
    },
    {
      id: 'tls-security',
      title: 'TLS Security Config',
      icon: Settings,
      description: "Utilizza le linee guida del TLS Observatory di Mozilla per verificare la sicurezza della configurazione TLS. Controlla configurazioni errate che possono rendere il sito vulnerabile ad attacchi, e fornisce consigli su come correggerle.",
      useCases: "Comprendere i problemi con la configurazione TLS di un sito ti aiuterà ad affrontare potenziali vulnerabilità e garantire che il sito stia usando la configurazione TLS più recente e sicura."
    },
    {
      id: 'tls-handshake',
      title: 'TLS Handshake Simulation',
      icon: Wifi,
      description: "Simula come diversi client (browser, sistemi operativi) eseguirebbero un handshake TLS con il server. Aiuta a identificare problemi di compatibilità e configurazioni insicure.",
      useCases: "Utile per garantire che il server sia accessibile da una varietà di client e che non ci siano problemi di compatibilità con browser o sistemi operativi più vecchi.",
      links: [
        { name: 'TLS Handshakes (Cloudflare)', url: 'https://www.cloudflare.com/learning/ssl/what-happens-in-a-tls-handshake/' },
        { name: 'SSL Test (SSL Labs)', url: 'https://www.ssllabs.com/ssltest/' }
      ]
    },
    {
      id: 'screenshot',
      title: 'Screenshot',
      icon: Camera,
      description: "Cattura uno screenshot della pagina web che l'URL/IP risolve, e lo visualizza. Fornisce una rappresentazione visiva di come appare il sito.",
      useCases: "Può essere utile per vedere come appare un dato sito web, libero dai vincoli del tuo browser, IP o posizione geografica."
    }
  ];

  const tableOfContents = features.map((f, i) => ({
    number: i + 1,
    id: f.id,
    title: f.title
  }));

  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      <Header />
      <main className="pt-28 pb-16 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Back button */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Torna alla Home
          </Link>

          {/* Header Section */}
          <div className="mb-12">
            <h1 className="text-4xl font-bold text-white mb-4">Guida alle Analisi</h1>
            <p className="text-gray-400 text-lg leading-relaxed">
              Smart Web Check è uno strumento OSINT all-in-one per scoprire informazioni su qualsiasi sito web o host.
              Il report evidenzia potenziali vettori di attacco, misure di sicurezza esistenti e la rete di connessioni 
              all'interno dell'architettura di un sito. I risultati possono anche aiutare a ottimizzare le risposte del server, 
              configurare redirect, gestire cookie o perfezionare i record DNS.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-[#1a1a2e] rounded-xl border border-gray-800/50 p-6 mb-12">
            <h2 className="text-xl font-bold text-white mb-4">Indice dei Contenuti</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {tableOfContents.map((item) => (
                <a 
                  key={item.id}
                  href={`#${item.id}`}
                  className="text-sm text-gray-400 hover:text-red-400 transition-colors flex items-center gap-2"
                >
                  <span className="text-red-500 font-mono">{item.number}.</span>
                  {item.title}
                </a>
              ))}
            </div>
          </div>

          {/* Feature Sections */}
          {features.map((feature) => (
            <FeatureSection key={feature.id} {...feature} />
          ))}

          {/* External Tools Section */}
          <section className="mt-16">
            <h2 className="text-2xl font-bold text-white mb-6">Strumenti Esterni per Ulteriori Ricerche</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {[
                { name: 'SSL Labs Test', url: 'https://www.ssllabs.com/ssltest/' },
                { name: 'VirusTotal', url: 'https://www.virustotal.com/' },
                { name: 'Shodan', url: 'https://www.shodan.io/' },
                { name: 'Archive.org', url: 'https://archive.org/web/' },
                { name: 'URLScan.io', url: 'https://urlscan.io/' },
                { name: 'DNSDumpster', url: 'https://dnsdumpster.com/' },
                { name: 'BuiltWith', url: 'https://builtwith.com/' },
                { name: 'Similar Web', url: 'https://www.similarweb.com/' },
              ].map((tool) => (
                <a
                  key={tool.name}
                  href={tool.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#1a1a2e] border border-gray-800/50 rounded-lg p-4 hover:border-red-500/50 transition-all text-center"
                >
                  <span className="text-sm text-white">{tool.name}</span>
                </a>
              ))}
            </div>
          </section>

          {/* Footer */}
          <footer className="mt-16 pt-8 border-t border-gray-800/50 text-center">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Smart Web Check - Powered by Smart Info Cyber Security
            </p>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default AboutPage;
